import React, { createContext } from "react";
import useAuth from "../hooks/useAuth";
import { DataContextType } from "../types/contextTypes";
import api from "../configs/apiConfig";

const DataContext = createContext<DataContextType>({} as DataContextType);

export const DataProvider = ({ children }: { children: React.ReactNode }) => {
    const { forceLogoutUser } = useAuth();

    const handleAPIError = async (error: any) => {
        const errorCode = error?.response?.status;
        const errorMessage = error?.response?.data?.message || error.message;

        console.log(`${errorCode} Error with Message ${errorMessage} Received`);

        if (!errorMessage) {
            throw new Error("Unhandled Server Error");
        }

        // for a disabled user
        if (errorCode === 401 && errorMessage === "Your account is disabled") {
            forceLogoutUser();
            throw new Error("Your account is disabled.");
        }

        // for an expired session or token
        if (
            errorMessage === "Expired session" ||
            errorMessage === "Refresh token expired"
        ) {
            forceLogoutUser();
            throw new Error("Your session has expired! Please log in again.");
        }

        // for a degraded user
        if (errorCode === 403) {
            forceLogoutUser();
            throw new Error("User not authorized");
        }

        if (errorCode === 500) {
            throw new Error("Internal server error.");
        }

        // WhoIs handlers
        if (
            errorCode === 400 &&
            (errorMessage.includes("domain must be a valid domain name") ||
                errorMessage.includes("domain should not be empty"))
        ) {
            throw new Error("Invalid Domain");
        }

        throw new Error("An unknown error occurred.");
    };

    const fetchData = async (url: string) => {
        try {
            const response = await api.get(url);
            return response.data;
        } catch (error: any) {
            await handleAPIError(error);
        }
    };

    const postData = async (url: string, data: any) => {
        try {
            const response = await api.post(url, data);
            return response.data;
        } catch (error: any) {
            await handleAPIError(error);
        }
    };

    const patchData = async (url: string, data: any) => {
        try {
            const response = await api.patch(url, data);
            return response.data;
        } catch (error: any) {
            await handleAPIError(error);
        }
    };

    const deleteData = async (url: string) => {
        try {
            const response = await api.delete(url);
            return response.data;
        } catch (error: any) {
            await handleAPIError(error);
        }
    };

    return (
        <DataContext.Provider
            value={{ fetchData, postData, patchData, deleteData }}
        >
            {children}
        </DataContext.Provider>
    );
};

export default DataContext;
